import { useMemo, useState } from "react";
import moment from "moment";
import { Button, Modal } from "src/components";
import Badge from "../../components/Badge";
import Reject from "src/features/fields/assets/Reject.svg";
import usePermission from "src/hooks/usePermission";
import { fineGrainedPermissionsKeys } from "src/constants/permission";
// PO-REJECTION-RESTRUCTURE ==================================================
import InvoiceDropdown from "src/pages/invoice-homepage/invoice-menu.component";
import PoRejectModal from "./PoRejectModal";
import PoRejectionRevokeModal from "./PoRejectionRevokeModal";
import PoRejectionDetailsCard from "./PoRejectionDetailsCard";
// PO-REJECTION-RESTRUCTURE ==================================================
// COUNTER-PO-REVERT ==================================================
import PoRevertModal from "./PoRevertModal";
// COUNTER-PO-REVERT ==================================================

interface PoCardProps {
    data: any;
    onViewDetails: () => void;
    // PO-REJECTION-RESTRUCTURE: refetches the list after a reject/revoke/re-edit action succeeds
    onRefresh?: () => void;
    // M10-15868: navigates to the linked order(s) when the "Order Codes" value is clicked
    onOrderCodeClick?: () => void;
}

const getStatusBadge = (status: any) => {
    const normalized = status?.toString()?.toLowerCase();
    let variant = "default";
    let valueToShow = status?.toString() || "Pending";

    if (normalized === "pending") {
        variant = "warning";
        valueToShow = "Pending";
        // PO-REJECTION-RESTRUCTURE: operation_status here is poid_status_hdr.poid_status_desc
        // (a free-text description like "Rejected"), not the lowercase order-domain ActionType
        // enum ("reject") — match by substring rather than exact equality.
    } else if (normalized?.includes("reject")) {
        variant = "error";
        valueToShow = "Rejected";
    } else if (normalized === "approval" || normalized === "approve") {
        variant = "success";
        valueToShow = "Approved";
    } else if (normalized === "processed") {
        variant = "success";
        valueToShow = "Processed";
    } else if (normalized === "post") {
        variant = "info";
        valueToShow = "Post";
    }

    return { variant, valueToShow };
};

const formatDateTime = (value: any) => {
    if (!value) return "-";
    return moment(value).format("D MMMM YYYY, hh:mm a");
};

const PoCard = ({ data, onViewDetails, onRefresh, onOrderCodeClick }: PoCardProps) => {
    const { variant, valueToShow } = getStatusBadge(data?.operation_status);

    // PO-REJECTION-RESTRUCTURE ==================================================
    // Dedicated per-action permissions under the Orders module (order:counter_po/reject
    // etc.) — this is the Orders-->PO tab's counter_po flow, not the separate
    // "Purchase Order" section's own "po" module. Same pattern as the invoice dropdown's permissionMap.
    const { hasPermission: hasPermissionReject } = usePermission({
        module: fineGrainedPermissionsKeys.ORDER.COUNTERPOREJECT.module,
        action: fineGrainedPermissionsKeys.ORDER.COUNTERPOREJECT.action,
    });
    const { hasPermission: hasPermissionRejectionRevoke } = usePermission({
        module: fineGrainedPermissionsKeys.ORDER.COUNTERPOREJECTIONREVOKE.module,
        action: fineGrainedPermissionsKeys.ORDER.COUNTERPOREJECTIONREVOKE.action,
    });
    const { hasPermission: hasPermissionRejectionReEdit } = usePermission({
        module: fineGrainedPermissionsKeys.ORDER.COUNTERPOREJECTIONREEDIT.module,
        action: fineGrainedPermissionsKeys.ORDER.COUNTERPOREJECTIONREEDIT.action,
    });
    // operation_status is poid_status_hdr.poid_status_desc (e.g. "Rejected"), not the
    // lowercase ActionType enum — substring match, same reasoning as getStatusBadge above.
    const isRejectedStatus = !!data?.operation_status?.toString()?.toLowerCase()?.includes("reject");
    // M10-15868: poid_status_id 2 = Approved — an Approved PO can no longer be rejected.
    const isApprovedStatus = data?.poid_status_id === 2;
    const stockistPoAttachmentId = data?.stockist_po_attachment_id;

    // COUNTER-PO-REVERT ==================================================
    const { hasPermission: hasPermissionRevertPo } = usePermission({
        module: fineGrainedPermissionsKeys.ORDER.COUNTERPOREVERT.module,
        action: fineGrainedPermissionsKeys.ORDER.COUNTERPOREVERT.action,
    });
    // Nothing to revert on a PO that's already Pending — the backend itself enforces the
    // actual "no active linked order" rule.
    const normalizedOperationStatus = data?.operation_status?.toString()?.toLowerCase();
    const isPendingStatus = !normalizedOperationStatus || normalizedOperationStatus === "pending";
    // COUNTER-PO-REVERT ==================================================

    const [modalStates, setModalStates] = useState({ reject: false, rejectionRevoke: false, rejectionReEdit: false, revert: false });
    const handleCloseModal = (modalName: "reject" | "rejectionRevoke" | "rejectionReEdit" | "revert") => {
        setModalStates((prev) => ({ ...prev, [modalName]: false }));
    };
    const handleActionSuccess = () => {
        onRefresh?.();
    };

    const menuItems = useMemo(() => {
        if (!stockistPoAttachmentId) return [];
        if (isRejectedStatus) {
            const items: { label: string; id: string; icon: string }[] = [];
            if (hasPermissionRejectionRevoke) items.push({ label: "Revoke Rejection", id: "rejectionRevoke", icon: Reject });
            if (hasPermissionRejectionReEdit) items.push({ label: "Re-edit Rejection", id: "rejectionReEdit", icon: Reject });
            return items;
        }
        const items: { label: string; id: string; icon: string }[] = [];
        // M10-15868: an Approved PO has nothing left to reject
        if (hasPermissionRevertPo && !isPendingStatus) items.push({ label: "Revert PO", id: "revert", icon: Reject });
        return items;
    }, [stockistPoAttachmentId, isRejectedStatus, isApprovedStatus, isPendingStatus, hasPermissionReject, hasPermissionRejectionRevoke, hasPermissionRejectionReEdit, hasPermissionRevertPo]);

    const handleOptionClick = (item: any) => {
        if (item.id === "reject" || item.id === "rejectionRevoke" || item.id === "rejectionReEdit" || item.id === "revert") {
            setModalStates((prev) => ({ ...prev, [item.id]: true }));
        }
    };
    // PO-REJECTION-RESTRUCTURE ==================================================

    const details: [string, any][] = [
        ["Stockist Name", data?.stockist_name || "-"],
        ["Counter Name", data?.counter_name || "-"],
        ["Counter Code", data?.counter_code || "-"],
        ["Location Name", data?.location_name || "-"],
        ["FUID No", data?.fuid_no || "-"],
        ["Order Codes", data?.order_codes || "-"],
        ["PO Ref No", data?.poid_ref_number || "-"],
        ["PO Upload Date", formatDateTime(data?.created_date) || "-"],
        ["Entered By", data?.entered_by || "-"],
        ["Entered Date", formatDateTime(data?.entered_date) || "-"],
        ["Link Type", data?.novu_metadata?.counterType || "-"],
        ["Hospital Name", data?.novu_metadata?.hospitalName || "-"],
        ["Stockist Code (CMP)", data?.company_stockist_code || "-"],
        ["Stockist Code (MV)", data?.stockist_medvol_code || "-"],
    ];

    return (
        <div className="mb-4 bg-white border rounded-[0.625rem] overflow-hidden">
            <header className="flex flex-wrap items-center justify-between gap-y-2 w-full px-5 py-3">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <h1 className="font-semibold text-[1.1rem] mr-2">
                        <span className="text-gray-400 text-[1.1rem]">PO#</span> {data?.poid_number || "-"}
                    </h1>
                    <Badge variant={variant} className="capitalize">
                        {valueToShow}
                    </Badge>
                    <p className="text-[#8051FF] text-sm font-semibold border border-gray-400 rounded-full px-2">{data?.company_name || "-"}</p>
                </div>

                <div className="flex items-center gap-x-2">
                    <Button className="h-10 text-white bg-[#8051FF] shadow-sm" onClick={onViewDetails}>
                        View Details
                    </Button>
                    {/* PO-REJECTION-RESTRUCTURE ============================================ */}
                    {menuItems.length > 0 && (
                        <div className="w-10 h-10 text-white shadow-sm">
                            <InvoiceDropdown
                                placeholder="..."
                                menuItems={menuItems}
                                displayKey="label"
                                onOptionClick={handleOptionClick}
                                currentSelected={undefined}
                                setModalStates={setModalStates}
                            />
                        </div>
                    )}
                </div>
            </header>

            {/* <div className="flex flex-wrap items-center gap-x-6 gap-y-1 px-5 pb-3 text-sm text-[#49484C]">
                <span>{data?.stockist_name || "-"}</span>
                <span className="text-gray-300">•</span>
                <span>{data?.counter_name || "-"}</span>
                <span className="text-gray-300">•</span>
                <span>{data?.counter_code || "-"}</span>
            </div> */}

            {/* PO-REJECTION-RESTRUCTURE: only for a PO currently in Reject status, not merely one that has a rejection_code stamped (e.g. still pending review) */}
            {isRejectedStatus && <PoRejectionDetailsCard data={data} />}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-y-3 px-5 border-t pt-5 pb-5">
                {details.map(([label, value]) => (
                    <div key={label} className="flex items-baseline gap-x-2 text-sm">
                        <p className="text-[#49484C] w-[150px] shrink-0 whitespace-nowrap font-normal not-italic">{label} :</p>
                        {/* M10-15868: Order Codes navigates to the linked order(s) when clickable */}
                        {label === "Order Codes" && data?.order_codes && onOrderCodeClick ? (
                            <p
                                className="text-[#8051FF] font-semibold cursor-pointer hover:underline"
                                onClick={onOrderCodeClick}
                            >
                                {value}
                            </p>
                        ) : (
                            <p className="text-[#1B1A1F]">{value}</p>
                        )}
                    </div>
                ))}
            </div>

            {/* PO-REJECTION-RESTRUCTURE ============================================ */}
            {/* Modal renders an empty placeholder button in normal flow even while closed
                (see modal.component.tsx) — hidden here so it doesn't add blank space below
                the card, same as InvoiceHomeDetailCard does for its own Modals. */}
            <div className="hidden">
                <Modal isOpen={modalStates.reject} isHeader={false} onCloseModal={() => handleCloseModal("reject")} title="Reject PO">
                    <PoRejectModal
                        stockistPoAttachmentId={stockistPoAttachmentId}
                        companyCode={data?.company_code}
                        onCloseModal={() => handleCloseModal("reject")}
                        onSuccess={handleActionSuccess}
                    />
                </Modal>
                <Modal isOpen={modalStates.rejectionRevoke} isHeader={false} onCloseModal={() => handleCloseModal("rejectionRevoke")} title="Revoke Rejection">
                    <PoRejectionRevokeModal
                        stockistPoAttachmentId={stockistPoAttachmentId}
                        companyCode={data?.company_code}
                        onCloseModal={() => handleCloseModal("rejectionRevoke")}
                        onSuccess={handleActionSuccess}
                    />
                </Modal>
                <Modal isOpen={modalStates.rejectionReEdit} isHeader={false} onCloseModal={() => handleCloseModal("rejectionReEdit")} title="Re-edit Rejection">
                    <PoRejectModal
                        mode="re-edit"
                        stockistPoAttachmentId={stockistPoAttachmentId}
                        companyCode={data?.company_code}
                        existingRejectionCode={data?.rejection_code}
                        existingRemarks={data?.rejection_data_desc?.remarks}
                        onCloseModal={() => handleCloseModal("rejectionReEdit")}
                        onSuccess={handleActionSuccess}
                    />
                </Modal>
                {/* COUNTER-PO-REVERT ============================================ */}
                <Modal isOpen={modalStates.revert} isHeader={false} onCloseModal={() => handleCloseModal("revert")} title="Revert PO">
                    <PoRevertModal
                        stockistPoAttachmentId={stockistPoAttachmentId}
                        companyCode={data?.company_code}
                        onCloseModal={() => handleCloseModal("revert")}
                        onSuccess={handleActionSuccess}
                    />
                </Modal>
                {/* COUNTER-PO-REVERT ============================================ */}
            </div>
            {/* PO-REJECTION-RESTRUCTURE ============================================ */}
        </div>
    );
};

export default PoCard;
